<script lang="ts">
	import type { ContactActionType } from "@components/ContactActionType.js";
	import ContactCard from "@components/ContactCard.svelte";
	import ContactCardActions from "@components/ContactCardActions.svelte";
	import HugeHeading from "@components/HugeHeading.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import { hugeHeadingParallax } from "@utils/hugeHeadingParallax.js";
	import copy from "@core/assets/icons/copy.svg?raw";
	import envelope from "@core/assets/icons/envelope.svg?raw";
	import invoice from "@core/assets/icons/invoice.svg?raw";
	import phone from "@core/assets/icons/phone.svg?raw";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import { Locale, type LocalizedString } from "@core/schema/Locale.js";
	import { companyAddressLines } from "../contacts/companyAddress.js";
	import { companyDic } from "../contacts/companyDic.js";
	import { companyIco } from "../contacts/companyIco.js";
	import { companyPhone } from "../contacts/companyPhone.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import type { ContactProps } from "./ContactProps.js";

	interface CompanyInfo {
		label: LocalizedString;
		value: string;
	}

	type Branch = {
		title: string;
		addressLines: string[];
	} & ({ mapsUrl: string } | { companyDetails: CompanyInfo[] });

	const { contacts }: ContactProps = $props();

	let activeContactIndex = $state<number>();
	let copiedIndex = $state<number>();

	const { tenant } = getGlobalTemplateProps();

	let copiedBranch = $state.raw<Branch>();
	let hoverLockedBranch = $state.raw<Branch>();

	let copiedCompanyId = $state.raw<CompanyInfo>();
	let hoverLockedCompanyId = $state.raw<CompanyInfo>();

	const contactItems: {
		actionType: ContactActionType;
		icon: string;
		label: LocalizedString;
		value: string;
	}[] = [
		{ actionType: "phone", icon: phone, label: { [Locale.cs]: "Telefon" }, value: companyPhone },
		{ actionType: "email", icon: invoice, label: { [Locale.cs]: "Fakturace" }, value: "fakturace@commarec.cz" },
		{ actionType: "email", icon: envelope, label: { [Locale.cs]: "E-mail" }, value: "info@commarec.cz" },
	];

	const branches: Branch[] = [
		{
			title: "Kancelář",
			addressLines: ["Krapkova 1159/3", "779 00 Olomouc", "Kancelář 605, 607 a 612", "(6. patro)"],
			mapsUrl: "https://maps.app.goo.gl/EMXFj2N5yRP1Y6Ey8",
		},
		{
			title: "Sklad",
			addressLines: ["Babíčková 8a", "779 00 Olomouc", "Česká republika"],
			mapsUrl: "https://maps.app.goo.gl/yu6T3UifEJWhy7xJ7",
		},
		{
			title: "Fakturační adresa & sídlo",
			addressLines: companyAddressLines,
			companyDetails: [
				{ label: { [Locale.cs]: "IČ" }, value: companyIco },
				{ label: { [Locale.cs]: "DIČ" }, value: companyDic },
			],
		},
	];
</script>

<Layout>
	<section class="my-8 md:my-16 lg:my-20 full-width mx-3 md:mx-8">
		<div class="flex flex-col 2xl:flex-row 2xl:gap-x-24 max-w-[120rem] 2xl:mx-auto">
			<div class="w-full 2xl:w-[84rem]">
				<HugeHeading
					level={1}
					class="text-center 2xl:text-left mb-0"
					text="stroke[[Od lepší logistiky nl[[]] vás dělí]] jeden e-mail nl[[]]nebo telefonát"
				/>
			</div>
			<div class="flex justify-center 2xl:justify-start items-center w-full 2xl:w-auto">
				<p
					class="my-12 md:my-16 2xl:my-0 font-semibold text-[1rem]/[1.4] md:text-[1.25rem]/[1.4] text-center 2xl:text-left"
				>
					<Formatted text="Kam nám napsat, zavolat nl[[]] a kde nás najdete?" />
				</p>
			</div>
		</div>
		<div class="2xl:huge-heading" use:hugeHeadingParallax></div>
		<div class="flex flex-col lg:flex-row gap-2 justify-center">
			{#each contactItems as item, index (index)}
				<div
					class="overflow-hidden w-full 2xl:w-[33rem] relative h-[14rem] hover:bg-white transition-colors duration-300 rounded-3xl"
				>
					<button
						type="button"
						class={[
							"bg-white/50 backdrop-blur-[8px] p-4 size-full text-left transition-colors duration-700",
							copiedIndex === index && "!bg-cmcsuccess hover:!bg-cmcsuccess",
						]}
						onclick={(): void => {
							activeContactIndex = index;
						}}
					>
						<div class="mb-8 md:mb-12 lg:mb-16 flex items-center justify-start">
							<Icon icon={item.icon} class="size-12 md:size-16 text-cmcprimary" />
						</div>
						<div class="flex flex-col gap-2">
							<p class="font-medium leading-[1.6] uppercase">{item.label[tenant.locale]}</p>
							<p class="font-semibold leading-[1.6] text-cmcsecondary text-[1.25rem]">{item.value}</p>
						</div>
					</button>

					<ContactCardActions
						direction="horizontal"
						isOpen={activeContactIndex === index}
						actionType={item.actionType}
						value={item.value}
						onclose={(reason): void => {
							activeContactIndex = undefined;
							if (reason === "copy") {
								copiedIndex = index;

								setTimeout(() => {
									if (copiedIndex === index) {
										copiedIndex = undefined;
									}
								}, 1000);
							}
						}}
					/>
				</div>
			{/each}
		</div>
	</section>

	<section class="full-width my-8 md:my-16 lg:my-20 mx-3 md:mx-8">
		<HugeHeading level={2} class="text-left md:text-center" text="stroke[[Kde nás]] najdete" />

		{#each branches as branch, index (branch)}
			<div
				class={[
					"flex flex-col xl:flex-row bg-white/50 backdrop-blur-[8px] p-4 rounded-3xl gap-2 justify-between w-full max-w-screen-xl mx-auto mb-2 xl:items-stretch",
					index === 0 && "-mt-3 md:-mt-5 lg:-mt-6 xl:-mt-11",
				]}
			>
				<div class="flex-1 w-full text-cmcsecondary md:text-[1.25rem] uppercase p-4">
					<Formatted text={branch.title} />
				</div>

				<div class="flex-1 w-full">
					<div
						class="relative h-full group overflow-hidden"
						role="presentation"
						tabindex="-1"
						onmouseleave={(): void => {
							if (hoverLockedBranch === branch) {
								hoverLockedBranch = undefined;
							}
						}}
					>
						<div
							class={[
								"p-4 rounded-lg md:text-[1.25rem] text-cmcsecondary font-semibold transition-colors duration-700 h-full",
								copiedBranch === branch ? "bg-cmcsuccess" : "bg-white",
							]}
						>
							<Formatted text={branch.addressLines.join("nl[[]]")} />
						</div>

						<button
							type="button"
							class={[
								"absolute inset-0 flex items-center justify-center rounded-lg bg-white transition-transform duration-200 ease-in-out translate-y-full",
								hoverLockedBranch !== branch && "group-hover:translate-y-0",
								copiedBranch === branch && "!opacity-0 !pointer-events-none",
							]}
							onclick={async (): Promise<void> => {
								await navigator.clipboard.writeText(branch.addressLines.join(", "));
								copiedBranch = branch;
								hoverLockedBranch = branch;

								setTimeout(() => {
									if (copiedBranch === branch) {
										copiedBranch = undefined;
									}
								}, 1000);
							}}
						>
							<div class="flex items-center gap-3">
								<Icon icon={copy} class="size-12 text-cmcprimary" />
								<span class="font-semibold">Zkopírovat kontakt do schránky</span>
							</div>
						</button>
					</div>
				</div>

				{#if "mapsUrl" in branch}
					<div class="flex-1">
						<Link
							variant={ButtonVariant.Outlined}
							href={branch.mapsUrl}
							target="_blank"
							rel="noopener"
							icon="arrow"
							class="flex items-center justify-between xl:h-40 p-4 w-full min-h-16 text-[0.875rem] md:text-[1rem]"
						>
							<Formatted text="Navigovat" />
						</Link>
					</div>
				{:else}
					<div class="flex flex-1 flex-col w-full gap-2">
						{#each branch.companyDetails as item (item)}
							{@const { label, value } = item as CompanyInfo}
							<div
								class="flex flex-1 relative group overflow-hidden"
								role="presentation"
								tabindex="-1"
								onmouseleave={(): void => {
									if (hoverLockedCompanyId === item) {
										hoverLockedCompanyId = undefined;
									}
								}}
							>
								<div
									class={[
										"flex rounded-lg justify-between items-center w-full p-4 transition-colors duration-700",
										copiedCompanyId === item ? "bg-cmcsuccess" : "bg-white",
									]}
								>
									<p class="leading-[1.6]">{label[tenant.locale]}</p>
									<p class="font-semibold leading-[1.6]">{value}</p>
								</div>

								<button
									type="button"
									class={[
										"absolute inset-0 flex items-center justify-center rounded-lg bg-white transition-transform duration-100 ease-out translate-y-full",
										hoverLockedCompanyId !== item && "group-hover:translate-y-0",
										copiedCompanyId === item && "!opacity-0 !pointer-events-none",
									]}
									onclick={async (): Promise<void> => {
										await navigator.clipboard.writeText(value);
										copiedCompanyId = item;
										hoverLockedCompanyId = item;
										setTimeout((): void => {
											if (copiedCompanyId === item) {
												copiedCompanyId = undefined;
											}
										}, 1000);
									}}
								>
									<div class="flex items-center gap-3">
										<Icon icon={copy} class="size-8 text-cmcprimary" />
										<span class="font-semibold">Zkopírovat do schránky</span>
									</div>
								</button>
							</div>
						{/each}
					</div>
				{/if}
			</div>
		{/each}
	</section>

	<section class="full-width my-8 md:my-16 lg:my-20 mx-3 md:mx-8">
		<HugeHeading level={2} class="text-right max-w-[116rem] mx-auto" text="stroke[[Kdo se o vás]] nl[[]] postará" />
		<div
			class="grid gap-2 mx-auto max-w-[100rem] sm:grid-cols-2 xl:grid-cols-3 -mt-3 md:-mt-5 lg:-mt-6 xl:-mt-11 justify-items-center"
		>
			{#each contacts as member (member.id)}
				<ContactCard {member} isImportant={member.isImportant} />
			{/each}
		</div>
	</section>
</Layout>
